import { useEffect, useState } from "react";
import { findVisibleTarget } from "@/lib/dom/findVisibleTarget";

/**
 * O primeiro elemento *visível* que casa com `selector`, ou `null` enquanto não há.
 *
 * O elemento pode ainda não ter sido pintado quando a novidade chega (a lista do menu depende
 * da conta e do papel; o alvo de um ponteiro só existe depois de abrir a conversa ou a aba),
 * pode sumir (menu recolhido no celular) e pode ser trocado por outro nó. Por isso a busca se
 * repete quando o DOM muda ou a janela é redimensionada, agrupada em um quadro por
 * `requestAnimationFrame`, e só atualiza o estado quando o elemento de fato mudou.
 *
 * @param {string|null} selector `null` desliga a busca
 * @returns {Element|null}
 */
export function useVisibleElement(selector) {
  const [element, setElement] = useState(null);

  useEffect(() => {
    if (!selector) {
      setElement(null);
      return undefined;
    }
    let frame = 0;

    const resolve = () => {
      frame = 0;
      const next = findVisibleTarget(selector);
      setElement((previous) => (previous === next ? previous : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(resolve);
    };

    resolve();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", schedule);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [selector]);

  return element;
}

/** Aspas e barras invertidas de um valor de atributo, para montar o seletor com segurança. */
export const attributeSelector = (attribute, value) => `[${attribute}="${String(value).replace(/["\\]/g, "\\$&")}"]`;
